import { Button } from 'components/Button'
import { useSuperAdmin } from 'hooks/useSuperAdmin'
import useShareSheetV2 from 'hooks/useShareSheetV2'
import { getInitials } from 'utils'
import { useAuth } from 'hooks/UseAuth'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { FileAccessGrant } from 'types'
import { RecipientsSelectorValueType } from 'types'
import { ReactComponent as GroupIcon } from 'components/ShareSheet/icons/Editors.svg'
import Avatar from 'common/Avatar/Avatar'
import Styles from '../ShareSheet.module.scss'
import { SubheaderProps } from '../Types'

const InheritedAccessRow = ({ accessGrant }: { accessGrant: FileAccessGrant }) => {
  const { isMobile } = useResponsiveDesign()
  const auth = useAuth()
  const {
    accessor: { type, name },
    access,
  } = accessGrant

  const accessorName = type === RecipientsSelectorValueType.UnauthUser ? 'public link access' : name

  return (
    <div className={Styles.inheritedAccessRow}>
      <>
        {!isMobile && <Avatar url={auth?.avatar} label={getInitials(auth?.display_name)} size={20} staticColor />}

        <div>
          You have {access} access via{' '}
          {type !== 'Channel' && (
            <Tooltip label={accessorName} hide={accessorName.length <= 30}>
              <span className={Styles.truncatedAccessorName}>{accessorName}</span>
            </Tooltip>
          )}
        </div>

        {type === 'Channel' && (
          <div className={Styles.accessContainer}>
            <GroupIcon />
            <Tooltip label={accessorName} hide={accessorName.length <= 30}>
              <span className={Styles.truncatedAccessorName}>{name}</span>
            </Tooltip>
          </div>
        )}
      </>
    </div>
  )
}

export const Subheader = ({ type, file }: SubheaderProps) => {
  const { superAdminMode } = useSuperAdmin()
  const { setShareSheet, currentUserHighestAccess, currentUserInheritedAccessGrant, asset } = useShareSheetV2(file)

  const setTakeOwnership = () => setShareSheet({ takeOwnership: true })

  const buildSubheader = () => {
    if (!type || type === 'article') return

    if (type === 'folder')
      return 'Invited members will have the same access for subfolders and documents in this folder.'

    if (type === 'layer')
      return 'Editors on this layer will be given Read access to main doc. Members need Edit access on main doc to merge a layer.'
  }

  if (superAdminMode)
    return (
      <div className={Styles.adminSubheaderContainer}>
        <span>You’re looking at this as a super admin.</span>

        {currentUserHighestAccess !== 'owner' && !asset?.default_folder && type !== 'layer' && (
          <Button variant="tertiary" size="sm" onPress={setTakeOwnership}>
            Take ownership
          </Button>
        )}
      </div>
    )

  const subheader = buildSubheader()

  if (!subheader && !currentUserInheritedAccessGrant) return null

  return (
    <div className={Styles.subheaderContainer}>
      {subheader && <span>{subheader}</span>}
      {currentUserInheritedAccessGrant && (
        <div className={Styles.inheritedContainer}>
          <InheritedAccessRow accessGrant={currentUserInheritedAccessGrant} />
        </div>
      )}
    </div>
  )
}
